import { AJUDA_CATALOGO } from "@/lib/ajuda/dialogos/catalogo";
import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/cn";
import { EquipamentoGastoTab } from "./EquipamentoGastoTab";

const inputCls =
  "h-9 w-full rounded-md border border-zinc-200 bg-white px-3 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-border dark:bg-card dark:text-foreground";
const labelCls = "mb-1 block text-xs font-medium text-zinc-600 dark:text-muted-foreground";

export type EquipamentoEditTarget = {
  id: string;
  name: string;
  notes: string | null;
  materialOptionIds: string[];
};

/**
 * Cadastro de equipamento — dados e gasto, em duas abas.
 *
 * O gasto só existe na edição, pelo mesmo motivo da ficha técnica do serviço: ele se pendura num
 * equipamento, e quem está criando ainda não tem nenhum.
 */
export function EquipamentoDialog({
  open,
  onClose,
  editTarget,
}: {
  open: boolean;
  onClose: () => void;
  editTarget?: EquipamentoEditTarget | null;
}) {
  const utils = trpc.useUtils();
  const [name, setName] = useState("");
  const [notes, setNotes] = useState("");
  const [materialOptionIds, setMaterialOptionIds] = useState<string[]>([]);
  const [aba, setAba] = useState<"dados" | "gasto">("dados");

  const editando = !!editTarget;
  const ofertasQuery = trpc.prosthesis.catalog.materialOptions.useQuery(undefined, { enabled: open });

  useEffect(() => {
    if (!open) return;
    setName(editTarget?.name ?? "");
    setNotes(editTarget?.notes ?? "");
    setMaterialOptionIds(editTarget?.materialOptionIds ?? []);
    setAba("dados");
  }, [open, editTarget?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const salvar = trpc.equipamento.salvar.useMutation({
    onSuccess: () => {
      toast.success(editando ? "Equipamento atualizado." : "Equipamento adicionado.");
      utils.equipamento.listar.invalidate();
      utils.fichaTecnica.invalidate();
      onClose();
    },
    onError: (e) => toast.error(e.message || "Erro ao salvar equipamento."),
  });

  const alternar = (id: string) =>
    setMaterialOptionIds((v) => (v.includes(id) ? v.filter((x) => x !== id) : [...v, id]));

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className={cn(
          "flex max-h-[88dvh] flex-col gap-3 overflow-y-auto dark:bg-card",
          editando && aba === "gasto" ? "max-w-3xl" : "max-w-lg",
        )}
      >
        <DialogHeader>
          <DialogTitle ajuda={AJUDA_CATALOGO.equipamento}>{editando ? "Editar equipamento" : "Novo equipamento"}</DialogTitle>
        </DialogHeader>

        {editando && (
          <div className="flex gap-1 rounded-lg border border-zinc-200 bg-zinc-50 p-1 dark:border-border dark:bg-muted/40">
            {(
              [
                ["dados", "Nome e materiais"],
                ["gasto", "Gasto por material"],
              ] as const
            ).map(([chave, rotulo]) => (
              <button
                key={chave}
                type="button"
                onClick={() => setAba(chave)}
                className={cn(
                  "h-8 flex-1 rounded-md text-xs font-semibold transition-colors",
                  aba === chave
                    ? "bg-brand-600 text-white shadow-sm"
                    : "text-zinc-600 hover:text-zinc-900 dark:text-muted-foreground dark:hover:text-foreground",
                )}
              >
                {rotulo}
              </button>
            ))}
          </div>
        )}

        {editando && aba === "gasto" && editTarget ? (
          <EquipamentoGastoTab
            equipmentId={editTarget.id}
            equipmentName={name || editTarget.name}
            materialOptionIds={materialOptionIds}
          />
        ) : (
          <>
            <div>
              <p className={labelCls}>Nome</p>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex: Fresadora VHF K5, Forno Programat"
                className={inputCls}
              />
            </div>

            <div>
              <p className={labelCls}>Observações</p>
              <input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Opcional"
                className={inputCls}
              />
            </div>

            <div>
              <p className={labelCls}>Materiais que a máquina trabalha</p>
              <div className="grid grid-cols-3 gap-1.5">
                {(ofertasQuery.data ?? []).map((m: any) => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => alternar(m.id)}
                    title={m.label}
                    className={
                      materialOptionIds.includes(m.id)
                        ? "truncate rounded-md border border-brand-300 bg-brand-50 px-2.5 py-1 text-left text-xs font-medium text-brand-700 dark:border-primary/40 dark:bg-primary/10 dark:text-primary"
                        : "truncate rounded-md border border-zinc-200 bg-white px-2.5 py-1 text-left text-xs font-medium text-zinc-600 hover:border-brand-300 dark:border-border dark:bg-card dark:text-muted-foreground"
                    }
                  >
                    {m.label}
                  </button>
                ))}
              </div>
              <p className="mt-1 text-[11px] leading-snug text-zinc-500 dark:text-muted-foreground">
                Nenhum marcado = a máquina vale para qualquer material. É essa lista que decide onde
                ela aparece pra escolher na produção.
              </p>
            </div>

            <div className="flex justify-end gap-2 border-t border-zinc-100 pt-3 dark:border-border">
              <button
                type="button"
                onClick={onClose}
                className="inline-flex h-9 items-center rounded-md border border-zinc-200 px-3 text-sm font-medium text-zinc-600 hover:bg-zinc-50 dark:border-border dark:text-muted-foreground"
              >
                Cancelar
              </button>
              <button
                type="button"
                disabled={salvar.isPending || name.trim().length < 2}
                onClick={() =>
                  salvar.mutate({
                    id: editTarget?.id,
                    name: name.trim(),
                    notes: notes.trim() || null,
                    materialOptionIds,
                  })
                }
                className="inline-flex h-9 items-center gap-1.5 rounded-md bg-brand-600 px-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-brand-700 disabled:opacity-50"
              >
                {salvar.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                {editando ? "Salvar" : "Adicionar"}
              </button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
